/**
 * PATH: packages/ui/src/components/notification-center/workforceFeed.ts
 * FILE_ID: UI-NOTIFCENTER-WORKFORCE-FEED-001
 * PURPOSE: Pure helpers that fold the workforce-api notification feed
 *          (timesheet submitted, approved, rejected, waiting summary) into the
 *          main dashboard bell: URL, item mapping, merge, and id routing.
 * WHY: #1237. A manager's submitted-timesheet alert lived only in the Diary
 *      bell (workforce DB). The dashboard bell reads Django, so the manager
 *      "never got a notification" while one sat unread in the other store.
 *      Workforce ids are prefixed so markAsRead can route them back.
 * FLOW: workforceFeedUrl(orgId) -> GET -> mapWorkforceItems -> mergeFeeds
 */

import type { NotificationItem } from './types'

export const WORKFORCE_ID_PREFIX = 'wf:'
const WORKFORCE_API_PREFIX = '/workforce-api/api/v1'
const FALLBACK_ORIGIN = 'https://quotech-backend.quotech.ai'

/** Pure: the workforce-api base. The dashboard axios client is same-origin
 * (API_BASE ''), and the nginx proxy forwards /api/ only, so this is absolute,
 * like src/app/ops/opsWorkforceHttp.ts. */
export function resolveWorkforceApiBase(env: Record<string, string | undefined>): string {
  const explicit = (env.VITE_WORKFORCE_API_URL || '').trim()
  if (explicit) return explicit.replace(/\/+$/, '')
  const origin = (env.VITE_BACKEND_ORIGIN || FALLBACK_ORIGIN).trim().replace(/\/+$/, '')
  return `${origin}${WORKFORCE_API_PREFIX}`
}

/** Pure: feed URL for one org, or null when no org is selected. */
export function workforceFeedUrl(base: string, orgId: string | number | null | undefined, limit = 50): string | null {
  if (orgId == null || String(orgId).trim() === '') return null
  return `${base}/notifications?company_id=${encodeURIComponent(String(orgId))}&limit=${limit}`
}

/** Pure: mark-read URL for a prefixed bell id, or null for a Django id. */
export function workforceReadUrl(base: string, orgId: string | number | null | undefined, bellId: string): string | null {
  if (!isWorkforceId(bellId) || orgId == null) return null
  const raw = bellId.slice(WORKFORCE_ID_PREFIX.length)
  return `${base}/notifications/${encodeURIComponent(raw)}/read?company_id=${encodeURIComponent(String(orgId))}`
}

export function isWorkforceId(id: string | number): boolean {
  return String(id).startsWith(WORKFORCE_ID_PREFIX)
}

export interface WorkforceFeedItem {
  id: string
  type?: string | null
  title?: string | null
  message?: string | null
  body?: string | null
  is_read?: boolean
  read_at?: string | null
  action_url?: string | null
  created_at?: string | null
}

const SEVERITY: Record<string, NotificationItem['severity']> = {
  timesheet_rejected: 'warning',
  timesheet_approved: 'success',
}

/** Pure: workforce action_url ("/approvals?worker=…") -> master-shell path. */
export function workforceActionUrl(url: string | null | undefined): string | null {
  if (!url) return null
  if (/^https?:\/\//.test(url) || url.startsWith('/workforce')) return url
  return `/workforce${url.startsWith('/') ? url : `/${url}`}`
}

/** Pure: feed rows -> bell rows (prefixed ids, operational category). */
export function mapWorkforceItems(rows: readonly WorkforceFeedItem[]): NotificationItem[] {
  return rows
    .filter((r) => r && r.id)
    .map((r) => {
      const type = String(r.type || 'general')
      return {
        id: `${WORKFORCE_ID_PREFIX}${r.id}`,
        category: 'operational',
        severity: SEVERITY[type] ?? 'info',
        title: r.title || 'Workforce',
        message: r.message || r.body || '',
        action_url: workforceActionUrl(r.action_url),
        is_read: r.is_read ?? !!r.read_at,
        read_at: r.read_at ?? null,
        created_at: r.created_at || new Date(0).toISOString(),
        metadata: { source: 'workforce', workforce_type: type },
      }
    })
}

/** Pure: one list, newest first. Django rows keep their ids untouched. */
export function mergeFeeds(django: readonly NotificationItem[], workforce: readonly NotificationItem[]): NotificationItem[] {
  return [...django, ...workforce].sort(
    (a, b) => Date.parse(b.created_at || '') - Date.parse(a.created_at || ''),
  )
}

/** Pure: unread badge = Django total_unread + unread workforce rows. */
export function mergedUnread(djangoUnread: number, workforce: readonly NotificationItem[]): number {
  return Math.max(0, djangoUnread || 0) + workforce.filter((n) => !n.is_read).length
}
